import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { forwardRef } from 'react'

import type { SectionContentWrapperProps } from './section-content-wrapper.types'

/**
 * `bp-desktop-cap` — the single global cap for in-page DS section **backgrounds**
 * (STRFRNT-16552, Figma node 11837:159739 "Max Width Updates", Part 1).
 *
 * Legacy caps the whole page wrapper at 1600px (`.mainWrapper` in
 * `dynamic-client-root-layout.module.scss`), so on production nothing — background
 * bands included — paints past 1600. The DS root layout deliberately drops that
 * wrapper cap, which let every coloured section band bleed to the viewport edge and
 * produced the "Ribbon" delta from production on ultra-wide screens.
 *
 * Part 1 restores parity by capping the band itself instead of the page wrapper: the
 * background stops expanding at the cap and centres, while content keeps its own
 * frame. At or below the cap this is a no-op — viewport and canvas agree, so the
 * render is pixel-identical to today.
 *
 * Change the cap HERE and it applies everywhere, because every capped band goes
 * through `SectionContentWrapper`. Part 3 of the design ("Expand Max Width", not
 * required by code freeze) is a value-only change to this constant: 1600px → 1920px.
 */
// eslint-disable-next-line i18next/no-literal-string -- layout max-width constant, not user-facing copy
export const DS_SECTION_BACKGROUND_CAP = '1600px'

/**
 * Stops a band expanding at `bp-desktop-cap` and centres it.
 *
 * Exported for the sections that cannot take an extra element and so apply the cap
 * to a band they already render — those import it as `sectionContentFrameClass`
 * (`SectionRenderer/sections/section-content-frame.ts`). Prefer
 * `SectionContentWrapper` — this is the same class it applies, not a second
 * definition of the cap.
 *
 * Legacy caps `.mainWrapper` at the same width and the DS root layout deliberately
 * does not, so past the cap sections that do not cap themselves drift away from the
 * ones that do (the hero). Below the cap viewport and canvas agree, so applying this
 * is a no-op there.
 *
 * Not `SectionContentContainer`: that block adds its own responsive gutters plus a
 * fixed 1504px inner frame.
 */
export const sectionBandCapClass = css({
  maxWidth: DS_SECTION_BACKGROUND_CAP,
  marginInline: 'auto',
})

const bandClass = css({
  width: 'full',
  // Keep `width: full` inclusive of any gutter the caller adds rather than relying
  // solely on the global Panda reset.
  boxSizing: 'border-box',
})

/**
 * The one DS section band. Every in-page section that paints a background renders
 * through this wrapper, so the "Ribbon" cap is defined and changed in exactly one
 * place (`DS_SECTION_BACKGROUND_CAP`).
 *
 * This is the element that PAINTS the background — pass the background colour and
 * the section's vertical spacing via `className`. It is deliberately layout-only
 * otherwise: no gutters, no inner frame. Sections that also need the DS content
 * frame (responsive gutters + centred 1504px inner frame) use
 * `SectionContentContainer`, which composes this wrapper as its band.
 *
 * Capping the band never moves content. An inner frame already capped at 1504px
 * stays centred in the viewport whether the band is 1600px or full width, so at all
 * widths content renders exactly where it does today; only the background's extent
 * changes, and only above the cap.
 *
 * Pass `fullBleed` to keep bleeding to the viewport edge (header, footer, hero
 * media). The forwarded `ref` lands on the band, so impression / visibility
 * observers measure the whole section.
 */
// `HTMLElement` (not `HTMLDivElement`) because `as` can render a non-div band
// (e.g. `as="section"`); the ref resolves to the rendered element's type.
export const SectionContentWrapper = forwardRef<HTMLElement, SectionContentWrapperProps>(function SectionContentWrapper(
  { as: Component = 'div', children, className, fullBleed = false, ...rest },
  ref,
) {
  return (
    <Component ref={ref} className={cx(bandClass, !fullBleed && sectionBandCapClass, className)} {...rest}>
      {children}
    </Component>
  )
})
